// src/components/CorrelationPanel.jsx
// 롱 바스켓 vs 숏 바스켓 일간수익률 상관 — 최근 1년, 현 보유비중(사이드별 정규화) 기준.
import React, { useState, useEffect, useMemo } from "react";
import { portfolioExposure, isShort } from "../lib/exposure";
import { computeLongShortCorr } from "../lib/riskMetrics";

const num = (n, d = 2) => (n == null || isNaN(n)) ? "—" : Number(n).toFixed(d);

export default function CorrelationPanel({ holdings, marketData, exchangeRates, onCorr }) {
  const [hist, setHist] = useState(null);
  const [status, setStatus] = useState("idle");

  // 사이드별 양(+)의 가치비중
  const { longWeights, shortWeights } = useMemo(() => {
    if (!holdings?.length) return { longWeights: [], shortWeights: [] };
    const { rows } = portfolioExposure(holdings, marketData, exchangeRates);
    const lw = [], sw = [];
    rows.filter((r) => r.hasPrice).forEach((r) => {
      const w = Math.abs(r.signedKRW);
      if (!w) return;
      (isShort(r) ? sw : lw).push({ ticker: r.ticker, market: r.market || "US", w });
    });
    return { longWeights: lw, shortWeights: sw };
  }, [holdings, marketData, exchangeRates]);

  const symbolsKey = useMemo(
    () => [...longWeights, ...shortWeights].map((w) => `${w.ticker}:${w.market}`).sort().join(","),
    [longWeights, shortWeights]
  );

  useEffect(() => {
    if (!symbolsKey) { setHist(null); setStatus("idle"); return; }
    let alive = true;
    setStatus("loading");
    fetch(`/api/history?symbols=${encodeURIComponent(symbolsKey)}&range=1y&interval=1d`)
      .then((r) => r.json())
      .then((j) => { if (alive) { setHist(j); setStatus("done"); } })
      .catch(() => { if (alive) setStatus("error"); });
    return () => { alive = false; };
  }, [symbolsKey]);

  const m = useMemo(() => {
    if (!hist?.series) return null;
    return computeLongShortCorr({ longWeights, shortWeights, series: hist.series });
  }, [hist, longWeights, shortWeights]);

  const corr = m && !m.insufficient ? m.corr : null;

  useEffect(() => { if (onCorr) onCorr(corr); }, [corr, onCorr]);
  // 해석: 높을수록 숏이 롱을 잘 헤지(동반 하락) → 시장중립적
  const tone = corr == null ? "#888" : corr >= 0.6 ? "#0a7c59" : corr >= 0.3 ? "#b8860b" : "#c0392b";
  const verdict = corr == null ? ""
    : corr >= 0.6 ? "숏이 롱을 잘 헤지 — 시장중립적"
    : corr >= 0.3 ? "부분 헤지 — 방향성 노출 존재"
    : "롱·숏 따로 논다 — 헤지 약함, 종목선택 베팅";

  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{ marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: "#1a1a2e" }}>롱·숏 바스켓 상관</h3>
        <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>
          최근 1년 일간수익률 · 사이드별 가치비중(합=1) · 로컬통화 · 거래일 교집합
          {m && !m.insufficient && <> · 롱 {m.coveredLong}·숏 {m.coveredShort}종목 · {m.n}일</>}
        </div>
      </div>

      {status === "loading" && <Note>📊 시계열 불러오는 중…</Note>}
      {status === "error" && <Note>⚠️ 시계열 조회 실패 — 잠시 후 새로고침</Note>}
      {status === "idle" && <Note>롱·숏 양쪽에 보유 종목이 있어야 계산됩니다.</Note>}
      {status === "done" && m?.insufficient && <Note>데이터 부족 — 공통 거래일 {m.n || 0}일(최소 20일) · 롱 {m.coveredLong}·숏 {m.coveredShort}종목</Note>}

      {corr != null && (
        <div style={{ display: "flex", alignItems: "center", gap: 20, background: "#fff", border: "1px solid #e8e8e8", borderRadius: 12, padding: "18px 22px", borderTop: `3px solid ${tone}` }}>
          <div>
            <div style={{ fontSize: 10.5, color: "#888", letterSpacing: "0.03em", textTransform: "uppercase" }}>Corr (Long, Short)</div>
            <div style={{ fontSize: 30, fontWeight: 700, color: tone, lineHeight: 1.1 }}>{num(corr)}</div>
          </div>
          <div style={{ fontSize: 13, color: "#555", fontWeight: 600 }}>{verdict}</div>
        </div>
      )}
    </div>
  );
}

function Note({ children }) {
  return <div style={{ padding: "12px 14px", background: "#f7f7f9", border: "1px solid #e8e8e8", borderRadius: 10, fontSize: 12.5, color: "#666" }}>{children}</div>;
}
